import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  SimpleGrid,
  Button,
  Flex,
  Heading,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  IconButton,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  FormControl,
  FormLabel,
  Textarea,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Progress,
  Radio,
  RadioGroup,
  Stack
} from "@chakra-ui/react";
import { 
  FiCheckCircle, 
  FiXCircle, 
  FiEye, 
  FiClock,
  FiDollarSign,
  FiUsers,
  FiAlertTriangle,
  FiTrendingUp,
  FiPercent,
  FiShield,
  FiRefreshCw,
  FiCreditCard
} from "react-icons/fi";
import { useUserContext } from "../../../context/UserContext";
import { useAccessControl } from "../../../hooks/useAccessControl";
import { 
  useGetPendingAccountantApprovals, 
  useApproveAccountantBatch,
  useRejectAccountantBatch,
  useGetBatchStatus,
  useRetryFailedPayments,
  useGetCompanyWallet,
  useGetPaymentMethod,
  useGetMandateStatus,
  useUpdatePaymentMethod
} from "../../../services/payrollService";
import { useGetPlanInfo } from "../../../services/planService";
import { formatCurrency, getCurrencySymbol, getCurrencyForCountry } from "../../../utils/currency";
import { showSuccessAlert, showErrorAlert } from "../../../utils/alert";

// Helper function to format currency with currency code
const formatCurrencyWithCode = (amount, currencyCode) => {
  if (amount === null || amount === undefined) return '0';
  
  const currencySymbols = {
    'USD': '$',
    'NGN': '₦',
    'GHS': '₵',
    'ZAR': 'R',
    'KES': 'KSh',
    'GBP': '£'
  };
  
  const symbol = currencySymbols[currencyCode] || '$';
  const numericAmount = parseFloat(amount) || 0;
  
  const formattedAmount = numericAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return `${symbol}${formattedAmount}`;
};

const AccountantApprovals = () => {
  const { user } = useUserContext();
  const { canApproveAccountant, customerRoles } = useAccessControl();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isApproveOpen, onOpen: onApproveOpen, onClose: onApproveClose } = useDisclosure();
  const { isOpen: isRejectOpen, onOpen: onRejectOpen, onClose: onRejectClose } = useDisclosure();
  const { isOpen: isSuccessOpen, onOpen: onSuccessOpen, onClose: onSuccessClose } = useDisclosure();
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [approvalSuccessData, setApprovalSuccessData] = useState(null);
  const cancelRef = React.useRef();
  
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("white", "gray.700");

  // Real API data
  const { 
    data: pendingBatchesData, 
    isLoading: pendingBatchesLoading, 
    error: pendingBatchesError,
    refetch: refetchPendingBatches
  } = useGetPendingAccountantApprovals();
  
  const approveBatchMutation = useApproveAccountantBatch();
  const rejectBatchMutation = useRejectAccountantBatch();
  const retryPaymentMutation = useRetryFailedPayments();
  
  // Fetch fresh batch details when a batch is selected and modal is open
  const { 
    data: batchDetailsData, 
    isLoading: batchDetailsLoading,
    refetch: refetchBatchDetails
  } = useGetBatchStatus(selectedBatch?.batch_id, {
    enabled: !!selectedBatch?.batch_id && isOpen // Only fetch when modal is open
  });
  
  // Get plan info for default currency
  const { planInfo } = useGetPlanInfo();
  
  // Get wallet balance for approval confirmation
  const { data: walletData } = useGetCompanyWallet();
  
  // Get payment method preference and mandate status
  const { data: paymentMethodData } = useGetPaymentMethod();
  const { data: mandateStatusData, refetch: refetchMandateStatus } = useGetMandateStatus();
  
  // Check if mandate is active (handle both response structures)
  const mandate = mandateStatusData?.mandate;
  const mandateStatus = mandate?.status?.toLowerCase();
  const hasActiveMandate = 
    mandateStatus === "active" ||
    (mandateStatusData?.has_mandate === true && mandateStatus === "active") ||
    (mandate?.authorized_at && mandateStatus !== "pending" && mandateStatus !== "PENDING") ||
    mandateStatusData?.has_active_mandate === true ||
    paymentMethodData?.has_active_mandate === true ||
    false;
  
  // Refetch mandate status when modal opens to ensure fresh data
  React.useEffect(() => {
    if (isOpen) {
      refetchMandateStatus();
    }
  }, [isOpen, refetchMandateStatus]);
  
  // Pre-select payment method from preference if available, but user must still confirm
  React.useEffect(() => {
    if (paymentMethodData?.payment_method && !selectedPaymentMethod) {
      setSelectedPaymentMethod(paymentMethodData.payment_method);
    }
  }, [paymentMethodData]);
  
  // Get default currency from plan info
  const getDefaultCurrency = () => {
    if (planInfo?.country) {
      return getCurrencyForCountry(planInfo.country).code;
    }
    // Fallback to first batch currency or NGN
    return pendingBatchesData?.data?.[0]?.items?.[0]?.currency || 
           pendingBatchesData?.data?.[0]?.currency || 
           'NGN';
  };
  
  // Calculate total company cost for selected batch
  const calculateTotalCompanyCost = (batch) => {
    if (!batch) return 0;
    const netSalaries = batch.payment_summary?.total_net_salaries || batch.total_amount || 0;
    const transferCharges = batch.total_transfer_charges || 0;
    const processingFees = batch.processing_fees_paid || 0;
    const settlement = batch.payment_summary?.total_settlement_amount || 0;
    return netSalaries + transferCharges + processingFees + settlement;
  };
  
  // Check if wallet has sufficient funds
  const hasSufficientFunds = (batch) => {
    if (!batch || !walletData) return null;
    const totalCost = calculateTotalCompanyCost(batch);
    const availableBalance = walletData.available_balance ?? walletData.balance ?? walletData.current_balance ?? 0;
    return availableBalance >= totalCost;
  };
  
  // Process API data
  const pendingBatches = pendingBatchesData?.data || [];

  // Helper function to extract employee info (handles both flat and nested structures)
  const getEmployeeInfo = (item, fallbackItem = null) => {
    // If employee_id is an object, treat it as the employee object itself
    const employeeObj = typeof item.employee_id === 'object' && item.employee_id !== null 
      ? item.employee_id 
      : null;
    
    // Also check if there's a separate employee field
    const employee = item.employee || employeeObj;
    
    // Extract ID - handle various possible structures
    let employeeId = item.employee_id;
    if (typeof employeeId === 'object' && employeeId !== null) {
      // Try various ID field names - check if it's a string representation of an ID
      // Some objects might have toString() that returns the ID
      const objStr = String(employeeId);
      
      employeeId = employeeId._id || 
                   employeeId.employee_id || 
                   employeeId.id || 
                   employeeId.user_id ||
                   // Check if toString returns something useful (not [object Object])
                   (objStr && objStr !== '[object Object]' && !objStr.includes('Object') ? objStr : null) ||
                   // Try accessing via bracket notation for Mongoose documents
                   (employeeId['_id'] || employeeId['id'] || null);
    }
    
    // If we still don't have an ID and have an employee object, try extracting from it
    if ((!employeeId || employeeId === 'N/A') && employee) {
      employeeId = employee._id || employee.employee_id || employee.id || employee.user_id;
    }
    
    // Use fallback item if available (from selectedBatch) when current item has object employee_id
    const useFallback = fallbackItem && typeof item.employee_id === 'object' && typeof fallbackItem.employee_id === 'string';
    const sourceItem = useFallback ? fallbackItem : item;
    
    // Extract name - try various possible fields, prefer fallback if available
    let name = sourceItem.employee_name || 
               item.employee_name ||
               employee?.full_name || 
               employee?.name || 
               employee?.display_name;
    
    // Try first_name + last_name if name is not found
    if (!name && (employee?.first_name || employee?.last_name)) {
      name = [employee?.first_name, employee?.last_name].filter(Boolean).join(' ').trim();
    }
    
    name = name || 'N/A';
    
    // Extract email - prefer fallback if available
    const email = sourceItem.employee_email || 
                  item.employee_email ||
                  employee?.email || 
                  employee?.email_address || 
                  'N/A';
    
    // For ID, prefer fallback string ID if current is object
    if (useFallback && fallbackItem.employee_id) {
      employeeId = fallbackItem.employee_id;
    }
    
    // Ensure ID is always a string, never an object
    let finalId = 'N/A';
    if (employeeId) {
      if (typeof employeeId === 'object') {
        // Still an object? Try one more time to extract
        finalId = employeeId._id || employeeId.employee_id || employeeId.id || employeeId.user_id || 'N/A';
        // If still an object, try string conversion one more time
        if (typeof finalId === 'object') {
          const str = String(finalId);
          finalId = (str && str !== '[object Object]' && !str.includes('Object')) ? str : 'N/A';
        }
      } else {
        finalId = String(employeeId);
      }
    }
    
    return {
      name: name || 'N/A',
      id: finalId || 'N/A',
      email: email || 'N/A'
    };
  };

  const handleViewBatch = (batch) => {
    setSelectedBatch(batch);
    setApprovalNotes("");
    onOpen();
    // Refetch batch details to get latest payment status
    if (batch?.batch_id) {
      setTimeout(() => refetchBatchDetails(), 100);
    }
  };
  
  // Use fresh batch details if available, otherwise fall back to selectedBatch from list
  const displayBatch = batchDetailsData?.data || selectedBatch;

  const { updatePaymentMethodPayload, updatePaymentMethodIsLoading } = useUpdatePaymentMethod();

  const handleApproveBatch = async () => {
    if (!selectedBatch?.batch_id) return;
    
    // If payment method is different from preference, update it first
    if (selectedPaymentMethod !== paymentMethodData?.payment_method) {
      try {
        await updatePaymentMethodPayload({ payment_method: selectedPaymentMethod });
      } catch (error) {
        showErrorAlert("Failed to update payment method. Please try again.");
        return;
      }
    }

    // Proceed with approval, including payment method in payload
    approveBatchMutation.mutate(
      { 
        batchId: selectedBatch.batch_id,
        payment_method: selectedPaymentMethod 
      },
      {
        onSuccess: (responseData) => {
          // Handle nested response structure
          const data = responseData?.data || responseData || {};
          
          // Store success data for modal
          setApprovalSuccessData({
            batchId: selectedBatch.batch_id,
            batchName: selectedBatch.batch_name,
            paymentMethod: selectedPaymentMethod,
            totalAmount: selectedBatch.total_amount || calculateTotalNetPay(selectedBatch.items || selectedBatch.batch_items || []),
            currency: selectedBatch.currency || selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || getDefaultCurrency(),
            transactionRef: data?.transaction_reference || data?.payment_reference || data?.tx_ref || data?.transaction_id || data?.payment_id,
            employeeCount: selectedBatch.employee_count || (selectedBatch.items || selectedBatch.batch_items || [])?.length
          });
          
          // Close approval confirmation dialog
          onApproveClose();
          // Close main batch view modal
          onClose();
          setApprovalNotes("");
          
          // Reset payment method to default after approval
          if (paymentMethodData?.payment_method) {
            setSelectedPaymentMethod(paymentMethodData.payment_method);
          }
          
          // Open success modal
          onSuccessOpen();
          
          // Refetch pending batches to update the list
          setTimeout(() => {
            refetchPendingBatches();
          }, 500);
        },
        onError: (error) => {
          // Extract error message from response
          const errorData = error?.response?.data || error?.data || {};
          const message = errorData?.message || "Failed to approve batch. Please try again.";
          showErrorAlert(message);
        }
      }
    );
  };

  const handleApproveClick = () => {
    onApproveOpen();
  };

  const handleRejectBatch = () => {
    if (selectedBatch?.batch_id) {
      rejectBatchMutation.mutate({
        batchId: selectedBatch.batch_id, 
        rejection_reason: "Rejected by Accountant"
      }, {
        onSuccess: () => {
          onRejectClose();
          onClose();
        }
      });
      }
  };

  const handleRejectClick = () => {
    onRejectOpen();
  };

  const handleRetryPayment = (batch) => {
    if (!batch?.batch_id) {
      showErrorAlert('Invalid batch. Cannot retry payment.');
      return;
    }
    
    retryPaymentMutation.mutate(batch.batch_id, {
      onSuccess: (data) => {
        showSuccessAlert('Payment retry initiated successfully! The batch will be reprocessed.');
        // Refetch batch details to get updated status
        if (batch.batch_id) {
          setTimeout(() => refetchBatchDetails(), 1000);
        }
        // Close modal if open
        onClose();
      },
      onError: (error) => {
        const errorData = error?.response?.data || error?.data || {};
        const message = errorData?.message || errorData?.detail || 'Failed to retry payment. Please try again.';
        showErrorAlert(message);
      }
    });
  };

  const getCurrencySymbol = (currency) => {
    const symbols = {
      USD: "$",
      NGN: "₦",
      GHS: "₵",
      KES: "KSh",
      ZAR: "R"
    };
    return symbols[currency] || currency;
  };

  const calculateTotalTaxes = (items) => {
    return items?.reduce((total, item) => total + (item.tax_amount || 0), 0) || 0;
  };

  const calculateTotalNetPay = (items) => {
    return items?.reduce((total, item) => total + (item.net_salary || 0), 0) || 0;
  };

  // Show loading state
  if (pendingBatchesLoading) {
    return (
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Heading size="md" color="gray.800">
              Accountant Approvals
            </Heading>
            <Text color="gray.600" fontSize="sm">
              Loading approval data...
            </Text>
          </VStack>
        </Flex>
        <Box textAlign="center" py={8}>
          <Text color="gray.500">Loading pending approvals...</Text>
        </Box>
      </VStack>
    );
  }

  // Show error state
  if (pendingBatchesError) {
    return (
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Heading size="md" color="gray.800">
              Accountant Approvals
            </Heading>
            <Text color="gray.600" fontSize="sm">
              Error loading approval data
            </Text>
          </VStack>
        </Flex>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error loading approval data!</AlertTitle>
          <AlertDescription>
            {pendingBatchesError?.message || 'Failed to load pending approvals'}
          </AlertDescription>
        </Alert>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Flex justify="space-between" align="center">
        <VStack align="start" spacing={1}>
          {/* <Heading size="md" color="gray.800">
            Accountant Approvals
          </Heading>
          <Text color="gray.600" fontSize="sm">
            Financial review and approval of HR-approved payroll batches
          </Text> */}
        </VStack>
        <Badge colorScheme="blue" fontSize="sm" px={3} py={1}>
          {pendingBatches.length} Pending
        </Badge>
      </Flex>

      {/* Summary Statistics */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Pending Approvals</StatLabel>
          <StatNumber color="blue.500">{pendingBatches.length}</StatNumber>
          <StatHelpText>
            <StatArrow type="increase" />
            +1 this week
          </StatHelpText>
        </Stat>

        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Total Amount</StatLabel>
          <StatNumber color="green.500">
            {formatCurrencyWithCode(
              pendingBatches.reduce((total, batch) => total + (batch.total_amount || 0), 0), 
              pendingBatches[0]?.items?.[0]?.currency || pendingBatches[0]?.currency || getDefaultCurrency()
            )}
          </StatNumber>
          <StatHelpText>
            <StatArrow type="increase" />
            +12% from last month
          </StatHelpText>
        </Stat>

        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Total Taxes</StatLabel>
          <StatNumber color="orange.500">
            {formatCurrencyWithCode(
              pendingBatches.reduce((total, batch) => total + calculateTotalTaxes(batch.items), 0), 
              pendingBatches[0]?.items?.[0]?.currency || pendingBatches[0]?.currency || getDefaultCurrency()
            )}
          </StatNumber>
          <StatHelpText>
            <StatArrow type="increase" />
            +8% from last month
          </StatHelpText>
        </Stat>

        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Avg. Review Time</StatLabel>
          <StatNumber color="purple.500">1.2 days</StatNumber>
          <StatHelpText>
            <StatArrow type="decrease" />
            -0.3 days improvement
          </StatHelpText>
        </Stat>
      </SimpleGrid>

      {/* Financial Compliance Notice */}
      <Alert status="info" borderRadius="lg">
        <AlertIcon />
        <Box>
          <AlertTitle>Financial Compliance Review</AlertTitle>
          <AlertDescription>
            As an accountant, please verify all tax calculations, deductions, and financial accuracy before approving payroll batches.
          </AlertDescription>
        </Box>
      </Alert>

      {/* Pending Batches Table */}
      <Box
        bg={cardBg}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
      >
        <TableContainer>
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Batch Name</Th>
                <Th>Total Amount</Th>
                <Th>Total Taxes</Th>
                <Th>Employees</Th>
                <Th>HR Approved By</Th>
                <Th>HR Approved Date</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {pendingBatches.map((batch) => (
                <Tr key={batch.batch_id}>
                  <Td>
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="600" color="gray.800">
                        {batch.batch_name}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {batch.batch_id}
                      </Text>
                    </VStack>
                  </Td>
                  <Td>
                    <Text fontWeight="600" color="gray.800">
                      {formatCurrencyWithCode(batch.total_amount, batch.items?.[0]?.currency || batch.currency)}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontWeight="600" color="orange.600">
                      {formatCurrencyWithCode(calculateTotalTaxes(batch.items), batch.items?.[0]?.currency || batch.currency)}
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <FiUsers color="gray.500" />
                      <Text>{batch.employee_count}</Text>
                    </HStack>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.600">
                      {typeof batch.approved_by_hr === 'object' ? batch.approved_by_hr?.name || 'Unknown' : batch.approved_by_hr || 'Unknown'}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.600">
                      {new Date(batch.approved_by_hr_at).toLocaleDateString()}
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Tooltip label="Review Financial Details">
                        <IconButton
                          icon={<FiEye />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          aria-label="Review batch"
                          onClick={() => handleViewBatch(batch)}
                        />
                      </Tooltip>
                      
                      {/* Show different actions based on batch status */}
                      {batch.status === 'COMPLETED' ? (
                        <Tooltip label="Batch Completed">
                          <Badge colorScheme="green" variant="subtle">
                            Completed
                          </Badge>
                        </Tooltip>
                      ) : batch.status === 'FAILED' ? (
                        <Tooltip label="Retry Payment">
                          <IconButton
                            icon={<FiRefreshCw />}
                            size="sm"
                            variant="ghost"
                            colorScheme="orange"
                            aria-label="Retry payment"
                            onClick={() => handleRetryPayment(batch)}
                            isLoading={retryPaymentMutation.isPending}
                          />
                        </Tooltip>
                      ) : batch.status === 'REJECTED' ? (
                        <Tooltip label="Batch Rejected">
                          <Badge colorScheme="red" variant="subtle">
                            Rejected
                          </Badge>
                        </Tooltip>
                      ) : (
                        // For PENDING_ACCOUNTANT_APPROVAL or other statuses, show view button (approve/reject in modal)
                        <Tooltip label="View & Review Batch">
                            <IconButton
                            icon={<FiEye />}
                              size="sm"
                              variant="ghost"
                            colorScheme="blue"
                            aria-label="View batch details"
                            onClick={() => handleViewBatch(batch)}
                            />
                          </Tooltip>
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      {/* Empty State */}
      {pendingBatches.length === 0 && (
        <Alert status="info" borderRadius="lg">
          <AlertIcon />
          <Box>
            <AlertTitle>No Pending Approvals</AlertTitle>
            <AlertDescription>
              All HR-approved batches have been reviewed. New batches will appear here after HR approval.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Batch Review Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Financial Review: {displayBatch?.batch_name || selectedBatch?.batch_name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {(displayBatch || selectedBatch) && (
              <VStack spacing={6} align="stretch">
                {batchDetailsLoading && (
                  <Alert status="info" borderRadius="lg">
                    <AlertIcon />
                    <AlertDescription>Loading latest batch details...</AlertDescription>
                  </Alert>
                )}
                {/* Batch Summary */}
                <Box bg="blue.50" p={4} borderRadius="lg" border="1px solid" borderColor="blue.200">
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <Box>
                      <Text fontSize="sm" color="blue.700" fontWeight="500">Total Amount</Text>
                      <Text fontSize="lg" fontWeight="700" color="blue.900">
                        {formatCurrencyWithCode(
                          displayBatch?.total_amount || selectedBatch?.total_amount || 0,
                          displayBatch?.items?.[0]?.currency || displayBatch?.batch_items?.[0]?.currency || displayBatch?.currency || selectedBatch?.items?.[0]?.currency || selectedBatch?.batch_items?.[0]?.currency || selectedBatch?.currency
                        )}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="blue.700" fontWeight="500">Employee Count</Text>
                      <Text fontSize="lg" fontWeight="700" color="blue.900">
                        {displayBatch?.employee_count || selectedBatch?.employee_count || 0}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="blue.700" fontWeight="500">Created By</Text>
                      <Text fontSize="lg" fontWeight="700" color="blue.900">
                        {typeof (displayBatch || selectedBatch)?.created_by === 'object' 
                          ? (displayBatch || selectedBatch)?.created_by?.name || 'Unknown' 
                          : (displayBatch || selectedBatch)?.created_by || 'Unknown'}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                {/* Payment Summary - Always show when batch is selected */}
                {(displayBatch || selectedBatch) && (
                    <Box>
                    <Heading size="sm" color="gray.800" mb={4}>
                      Payment Summary
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                      <Box p={4} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                        <Text fontSize="sm" color="green.700" fontWeight="500" mb={1}>
                          Total Net Salaries
                        </Text>
                        <Text fontSize="xl" fontWeight="700" color="green.900">
                          {formatCurrencyWithCode(
                            displayBatch?.payment_summary?.total_net_salaries || displayBatch?.total_amount || selectedBatch?.payment_summary?.total_net_salaries || selectedBatch?.total_amount || 0,
                            displayBatch?.items?.[0]?.currency || displayBatch?.batch_items?.[0]?.currency || displayBatch?.currency || selectedBatch?.items?.[0]?.currency || selectedBatch?.batch_items?.[0]?.currency || selectedBatch?.currency
                          )}
                        </Text>
                        <Text fontSize="xs" color="green.600" mt={1}>
                          Employees Receive
                        </Text>
                      </Box>
                      <Box p={4} bg="purple.50" borderRadius="md" border="1px solid" borderColor="purple.200">
                        <Text fontSize="sm" color="purple.700" fontWeight="500" mb={1}>
                          Transfer Charges
                        </Text>
                        <Text fontSize="xl" fontWeight="700" color="purple.900">
                          {formatCurrencyWithCode(
                            displayBatch?.total_transfer_charges || selectedBatch?.total_transfer_charges || 0,
                            displayBatch?.items?.[0]?.currency || displayBatch?.batch_items?.[0]?.currency || displayBatch?.currency || selectedBatch?.items?.[0]?.currency || selectedBatch?.batch_items?.[0]?.currency || selectedBatch?.currency
                          )}
                        </Text>
                        <Text fontSize="xs" color="purple.600" mt={1}>
                          Payment Processing Fees
                        </Text>
                      </Box>
                      <Box p={4} bg="teal.50" borderRadius="md" border="1px solid" borderColor="teal.200">
                        <Text fontSize="sm" color="teal.700" fontWeight="500" mb={1}>
                          Processing Fee (0.5%)
                        </Text>
                        <Text fontSize="xl" fontWeight="700" color="teal.900">
                          {formatCurrencyWithCode(
                            displayBatch?.processing_fees_paid || selectedBatch?.processing_fees_paid || 0,
                            displayBatch?.items?.[0]?.currency || displayBatch?.batch_items?.[0]?.currency || displayBatch?.currency || selectedBatch?.items?.[0]?.currency || selectedBatch?.batch_items?.[0]?.currency || selectedBatch?.currency
                          )}
                        </Text>
                        {(() => {
                          const batch = displayBatch || selectedBatch;
                          const items = batch?.items || batch?.batch_items || [];
                          const employeeCount = items.length || batch?.payment_summary?.total_employees || 0;
                          const totalFee = displayBatch?.processing_fees_paid || selectedBatch?.processing_fees_paid || 0;
                          const perEmployeeFee = employeeCount > 0 ? totalFee / employeeCount : 0;
                          const currency = displayBatch?.items?.[0]?.currency || displayBatch?.batch_items?.[0]?.currency || displayBatch?.currency || selectedBatch?.items?.[0]?.currency || selectedBatch?.batch_items?.[0]?.currency || selectedBatch?.currency;
                          return employeeCount > 0 && perEmployeeFee > 0 ? (
                            <Text fontSize="xs" color="teal.600" mt={1}>
                              {formatCurrencyWithCode(perEmployeeFee, currency)} × {employeeCount} employees
                            </Text>
                          ) : (
                            <Text fontSize="xs" color="teal.600" mt={1}>
                              Service Fee
                            </Text>
                          );
                        })()}
                      </Box>
                      <Box p={4} bg="orange.50" borderRadius="md" border="1px solid" borderColor="orange.200">
                        <Text fontSize="sm" color="orange.700" fontWeight="500" mb={1}>
                          Total Settlement
                        </Text>
                        <Text fontSize="xl" fontWeight="700" color="orange.900">
                          {formatCurrencyWithCode(
                            displayBatch?.payment_summary?.total_settlement_amount || selectedBatch?.payment_summary?.total_settlement_amount || 0,
                            displayBatch?.items?.[0]?.currency || displayBatch?.batch_items?.[0]?.currency || displayBatch?.currency || selectedBatch?.items?.[0]?.currency || selectedBatch?.batch_items?.[0]?.currency || selectedBatch?.currency
                          )}
                        </Text>
                        <Text fontSize="xs" color="orange.600" mt={1}>
                          Company Pays (Tax + Deductions)
                      </Text>
                    </Box>
                      <Box p={4} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                        <Text fontSize="sm" color="blue.700" fontWeight="500" mb={1}>
                          Total Company Cost
                        </Text>
                        <Text fontSize="xl" fontWeight="700" color="blue.900">
                          {formatCurrencyWithCode(
                            (displayBatch?.payment_summary?.total_net_salaries || displayBatch?.total_amount || selectedBatch?.payment_summary?.total_net_salaries || selectedBatch?.total_amount || 0) + 
                            (displayBatch?.total_transfer_charges || selectedBatch?.total_transfer_charges || 0) +
                            (displayBatch?.processing_fees_paid || selectedBatch?.processing_fees_paid || 0) +
                            (displayBatch?.payment_summary?.total_settlement_amount || selectedBatch?.payment_summary?.total_settlement_amount || 0),
                            displayBatch?.items?.[0]?.currency || displayBatch?.batch_items?.[0]?.currency || displayBatch?.currency || selectedBatch?.items?.[0]?.currency || selectedBatch?.batch_items?.[0]?.currency || selectedBatch?.currency
                          )}
                        </Text>
                        <Text fontSize="xs" color="blue.600" mt={1}>
                          All Costs Combined
                      </Text>
                    </Box>
                      <Box p={4} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                        <Text fontSize="sm" color="gray.700" fontWeight="500" mb={1}>
                          Payment Status
                        </Text>
                        <Badge 
                          colorScheme={(displayBatch || selectedBatch)?.payment_summary?.ready_for_payment ? 'green' : 'yellow'} 
                          fontSize="md" 
                          p={2}
                        >
                          {(displayBatch || selectedBatch)?.payment_summary?.ready_for_payment ? 'Ready' : 'Not Ready'}
                        </Badge>
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          {(displayBatch || selectedBatch)?.payment_summary?.total_deductions || 0} Deductions
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>
                )}

                <Divider />

                {/* HR Approval Info */}
                <Box bg="blue.50" p={4} borderRadius="lg" border="1px solid" borderColor="blue.200">
                  <HStack spacing={4}>
                    <FiShield color="blue.500" size={20} />
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" color="blue.700" fontWeight="500">
                        HR Approved by: {typeof selectedBatch.approved_by_hr === 'object' ? selectedBatch.approved_by_hr?.name || 'Unknown' : selectedBatch.approved_by_hr || 'Unknown'}
                      </Text>
                      <Text fontSize="sm" color="blue.600">
                        Approved on: {new Date(selectedBatch.approved_by_hr_at).toLocaleString()}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>

                {/* Employee Financial Details */}
                <Box>
                  <Heading size="sm" color="gray.800" mb={4}>
                    Employee Financial Breakdown
                  </Heading>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Employee</Th>
                          <Th>Basic Salary</Th>
                          <Th>Allowances</Th>
                          <Th>Bonus</Th>
                          <Th>Leave Allowance</Th>
                          <Th>Deductions</Th>
                          <Th>Income Tax</Th>
                          <Th>Settlement Amount</Th>
                          <Th>Net Salary</Th>
                          <Th>Transfer Charge</Th>
                          <Th>Pay Type</Th>
                          <Th>Payment Status</Th>
                          <Th>Refund Amount</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {((displayBatch || selectedBatch)?.items?.length > 0 || (displayBatch || selectedBatch)?.batch_items?.length > 0) ? (
                          ((displayBatch || selectedBatch)?.items || (displayBatch || selectedBatch)?.batch_items || []).map((item, index) => {
                            // ALWAYS use selectedBatch for employee info (has correct structure with string employee_id and employee_name)
                            // Use displayBatch/item for financial data (amounts, fees, status)
                            const selectedItems = selectedBatch?.items || selectedBatch?.batch_items || [];
                            const selectedItem = selectedItems[index] || item; // Fallback to item if selectedItem doesn't exist
                            
                            // Always prefer selectedBatch for employee info - it has the correct structure
                            const employeeSourceItem = selectedItem && (selectedBatch?.items || selectedBatch?.batch_items)?.length > 0 
                              ? selectedItem 
                              : item;
                            
                            return (
                            <Tr key={index}>
                              <Td>
                                <VStack align="start" spacing={1}>
                                  {(() => {
                                    const empInfo = getEmployeeInfo(employeeSourceItem);
                                    return (
                                      <>
                                        <Text fontWeight="600" color="gray.800">
                                          {empInfo.name}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                          ID: {empInfo.id}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                          {empInfo.email}
                                        </Text>
                                      </>
                                    );
                                  })()}
                                </VStack>
                              </Td>
                              <Td>
                                {formatCurrencyWithCode(item.basic_salary || 0, item.currency || displayBatch?.currency || selectedBatch?.currency)}
                              </Td>
                              <Td>
                                {formatCurrencyWithCode(item.allowances || 0, item.currency || displayBatch?.currency || selectedBatch?.currency)}
                              </Td>
                              <Td>
                                <Text fontWeight="500" color="blue.600">
                                  {formatCurrencyWithCode(item.bonus || 0, item.currency || displayBatch?.currency || selectedBatch?.currency)}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontWeight="500" color="green.600">
                                  {formatCurrencyWithCode(item.leave_allowance || 0, item.currency || displayBatch?.currency || selectedBatch?.currency)}
                                </Text>
                              </Td>
                              <Td>
                                {formatCurrencyWithCode(item.deductions || 0, item.currency || displayBatch?.currency || selectedBatch?.currency)}
                              </Td>
                              <Td>
                                <Text color="red.600" fontWeight="500">
                                  {formatCurrencyWithCode(item.tax_amount || 0, item.currency || displayBatch?.currency || selectedBatch?.currency)}
                                </Text>
                              </Td>
                              <Td>
                                <Text color="orange.600" fontWeight="500">
                                  {formatCurrencyWithCode(item.settlement_amount || 0, item.currency || displayBatch?.currency || selectedBatch?.currency)}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontWeight="600" color="green.600">
                                  {formatCurrencyWithCode(item.net_salary || 0, item.currency || displayBatch?.currency || selectedBatch?.currency)}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontWeight="500" color="purple.600">
                                  {formatCurrencyWithCode(item.flutterwave_fee || 0, item.currency || displayBatch?.currency || selectedBatch?.currency)}
                                </Text>
                              </Td>
                              <Td>
                                <Badge colorScheme="blue" variant="subtle">
                                  {item.pay_type === 'regular_pay' ? 'Regular Pay' : 
                                   item.pay_type === 'total_pay' ? 'Total Pay' : 
                                   item.pay_type === 'net_salary' ? 'Net Salary' : 
                                   item.pay_type || 'Regular Pay'}
                                </Badge>
                              </Td>
                              <Td>
                                <Badge 
                                  colorScheme={
                                    item.payment_status === 'PAID' || item.payment_status === 'SUCCESS' || item.payment_status === 'COMPLETED' ? 'green' :
                                    item.payment_status === 'FAILED' ? 'red' :
                                    item.payment_status === 'PROCESSING' ? 'yellow' :
                                    'gray'
                                  } 
                                  variant="subtle"
                                >
                                  {item.payment_status || 'PENDING'}
                                </Badge>
                                {item.failure_reason && (
                                  <Text fontSize="xs" color="red.600" mt={1}>
                                    {item.failure_reason}
                                  </Text>
                                )}
                              </Td>
                              <Td>
                                {item.payment_status === 'FAILED' && item.refund_amount ? (
                                  <VStack align="start" spacing={0}>
                                    <Text fontSize="sm" fontWeight="600" color="red.600">
                                      {formatCurrencyWithCode(item.refund_amount, item.currency || displayBatch?.currency || selectedBatch?.currency)}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                      Will be refunded
                                    </Text>
                                  </VStack>
                                ) : (
                                  <Text fontSize="sm" color="gray.400">—</Text>
                                )}
                              </Td>
                            </Tr>
                            );
                          })
                        ) : (
                          <Tr>
                            <Td colSpan={11}>
                              <VStack spacing={2} py={4}>
                                <Text color="gray.500">No employee details available</Text>
                              </VStack>
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider />

                {/* Rejection Details - Show only for REJECTED batches */}
                {selectedBatch?.status === 'REJECTED' && (
                  <Box bg="red.50" p={4} borderRadius="lg" border="1px solid" borderColor="red.200">
                    <VStack align="stretch" spacing={3}>
                      <Heading size="sm" color="red.800">
                        Rejection Details
                      </Heading>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <Box>
                          <Text fontSize="sm" color="red.700" fontWeight="500">Rejected By</Text>
                          <Text fontSize="lg" fontWeight="700" color="red.900">
                            {selectedBatch.rejected_by || 'Unknown'}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color="red.700" fontWeight="500">Rejection Level</Text>
                          <Text fontSize="lg" fontWeight="700" color="red.900">
                            {selectedBatch.rejection_level || 'ACCOUNTANT'}
                          </Text>
                        </Box>
                      </SimpleGrid>
                      <Box>
                        <Text fontSize="sm" color="red.700" fontWeight="500">Rejection Reason</Text>
                        <Text fontSize="md" color="red.900" mt={1}>
                          {selectedBatch.rejection_reason || 'No reason provided'}
                        </Text>
                      </Box>
                      {selectedBatch.rejection_note && (
                        <Box>
                          <Text fontSize="sm" color="red.700" fontWeight="500">Additional Notes</Text>
                          <Text fontSize="md" color="red.900" mt={1}>
                            {selectedBatch.rejection_note}
                          </Text>
                        </Box>
                      )}
                      <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <AlertDescription>
                          This batch has been sent back to HR for re-approval.
                        </AlertDescription>
                      </Alert>
                    </VStack>
                  </Box>
                )}

                {/* Approval Notes */}
                <FormControl>
                  <FormLabel>
                    {selectedBatch?.status === 'PENDING_ACCOUNTANT_APPROVAL' 
                      ? 'Rejection Reason (Required for rejection)' 
                      : 'Financial Review Notes (Optional)'
                    }
                  </FormLabel>
                  <Textarea
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    placeholder={
                      selectedBatch?.status === 'PENDING_ACCOUNTANT_APPROVAL'
                        ? 'Please provide a reason for rejection...'
                        : 'Add any financial review notes or comments...'
                    }
                    rows={3}
                    isReadOnly={selectedBatch?.status === 'COMPLETED' || selectedBatch?.status === 'REJECTED'}
                    bg={selectedBatch?.status === 'COMPLETED' || selectedBatch?.status === 'REJECTED' ? 'gray.50' : 'white'}
                  />
                </FormControl>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            {selectedBatch?.status === 'COMPLETED' ? (
              // For COMPLETED batches, show only Close button
              <Button colorScheme="blue" onClick={onClose}>
                Close
              </Button>
            ) : selectedBatch?.status === 'FAILED' ? (
              // For FAILED batches, show Close and Retry buttons
              <>
                <Button variant="ghost" mr={3} onClick={onClose}>
                  Close
                </Button>
                <Button 
                  colorScheme="orange" 
                  onClick={() => handleRetryPayment(selectedBatch)}
                  isLoading={retryPaymentMutation.isPending}
                  loadingText="Retrying..."
                >
                  Retry Payment
                </Button>
              </>
            ) : selectedBatch?.status === 'REJECTED' ? (
              // For REJECTED batches, show only Close button (read-only)
              <Button colorScheme="blue" onClick={onClose}>
                Close
              </Button>
            ) : (
              // For PENDING_ACCOUNTANT_APPROVAL or other statuses, show original buttons
              canApproveAccountant() ? (
              <>
                <Button variant="ghost" mr={3} onClick={onClose}>
                  Cancel
                </Button>
                <Button colorScheme="red" mr={3} onClick={handleRejectClick}>
                  Reject Batch
                </Button>
                <Button colorScheme="green" onClick={handleApproveClick}>
                  Approve & Process
                </Button>
              </>
              ) : (
                <Button colorScheme="blue" onClick={onClose}>
                  Close
                </Button>
              )
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Approve Confirmation Dialog */}
      <AlertDialog
        isOpen={isApproveOpen}
        leastDestructiveRef={cancelRef}
        onClose={onApproveClose}
        size="xl"
      >
        <AlertDialogOverlay>
          <AlertDialogContent maxW="600px">
            <AlertDialogHeader fontSize="xl" fontWeight="bold" pb={2}>
              <HStack spacing={2}>
                <FiShield color="green" size={24} />
                <Text>Approve & Process Payroll Batch</Text>
              </HStack>
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack spacing={4} align="stretch">
                {/* Warning Message */}
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    This will initiate payment processing and cannot be undone. Please review all details before confirming.
                  </AlertDescription>
                </Alert>

                {/* Batch Info */}
                <Box p={3} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                  <VStack align="start" spacing={2}>
                    <HStack spacing={2}>
                      <FiUsers color="blue.600" size={16} />
                      <Text fontSize="sm" fontWeight="600" color="blue.900">
                        Batch: {selectedBatch?.batch_name}
                      </Text>
                    </HStack>
                    <HStack spacing={4}>
                      <Text fontSize="sm" color="blue.700">
                        Employees: <Text as="span" fontWeight="600">{selectedBatch?.employee_count || 0}</Text>
                      </Text>
                      <Text fontSize="sm" color="blue.700">
                        Currency: <Text as="span" fontWeight="600">{selectedBatch?.items?.[0]?.currency || selectedBatch?.batch_items?.[0]?.currency || selectedBatch?.currency || getDefaultCurrency()}</Text>
                      </Text>
                    </HStack>
                  </VStack>
                </Box>

                {/* Cost Breakdown */}
                {selectedBatch && (
                  <>
                    <Heading size="sm" color="gray.700" mb={2}>
                      Cost Breakdown
                    </Heading>
                    <SimpleGrid columns={2} spacing={3}>
                      <Box p={3} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                        <HStack spacing={2} mb={1}>
                          <FiDollarSign color="green.600" size={14} />
                          <Text fontSize="xs" color="green.700" fontWeight="500">
                            Net Salaries
                          </Text>
                        </HStack>
                        <Text fontSize="md" fontWeight="700" color="green.900">
                          {formatCurrencyWithCode(
                            selectedBatch.payment_summary?.total_net_salaries || selectedBatch.total_amount || 0,
                            selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency || getDefaultCurrency()
                          )}
                        </Text>
                      </Box>
                      <Box p={3} bg="purple.50" borderRadius="md" border="1px solid" borderColor="purple.200">
                        <HStack spacing={2} mb={1}>
                          <FiTrendingUp color="purple.600" size={14} />
                          <Text fontSize="xs" color="purple.700" fontWeight="500">
                            Transfer Charges
                          </Text>
                        </HStack>
                        <Text fontSize="md" fontWeight="700" color="purple.900">
                          {formatCurrencyWithCode(
                            selectedBatch.total_transfer_charges || 0,
                            selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency || getDefaultCurrency()
                          )}
                        </Text>
                      </Box>
                      <Box p={3} bg="teal.50" borderRadius="md" border="1px solid" borderColor="teal.200">
                        <HStack spacing={2} mb={1}>
                          <FiPercent color="teal.600" size={14} />
                          <Text fontSize="xs" color="teal.700" fontWeight="500">
                            Processing Fee
                          </Text>
                        </HStack>
                        <Text fontSize="md" fontWeight="700" color="teal.900">
                          {formatCurrencyWithCode(
                            displayBatch?.processing_fees_paid || selectedBatch?.processing_fees_paid || 0,
                            displayBatch?.items?.[0]?.currency || displayBatch?.batch_items?.[0]?.currency || displayBatch?.currency || selectedBatch?.items?.[0]?.currency || selectedBatch?.batch_items?.[0]?.currency || selectedBatch?.currency || getDefaultCurrency()
                          )}
                        </Text>
                        {(() => {
                          const batch = displayBatch || selectedBatch;
                          const items = batch?.items || batch?.batch_items || [];
                          const employeeCount = items.length || batch?.payment_summary?.total_employees || 0;
                          const totalFee = displayBatch?.processing_fees_paid || selectedBatch?.processing_fees_paid || 0;
                          const perEmployeeFee = employeeCount > 0 ? totalFee / employeeCount : 0;
                          const currency = displayBatch?.items?.[0]?.currency || displayBatch?.batch_items?.[0]?.currency || displayBatch?.currency || selectedBatch?.items?.[0]?.currency || selectedBatch?.batch_items?.[0]?.currency || selectedBatch?.currency || getDefaultCurrency();
                          return employeeCount > 0 && perEmployeeFee > 0 ? (
                            <Text fontSize="xs" color="teal.600" mt={1}>
                              {formatCurrencyWithCode(perEmployeeFee, currency)} × {employeeCount} employees
                            </Text>
                          ) : null;
                        })()}
                      </Box>
                      <Box p={3} bg="orange.50" borderRadius="md" border="1px solid" borderColor="orange.200">
                        <HStack spacing={2} mb={1}>
                          <FiAlertTriangle color="orange.600" size={14} />
                          <Text fontSize="xs" color="orange.700" fontWeight="500">
                            Settlement
                          </Text>
                        </HStack>
                        <Text fontSize="md" fontWeight="700" color="orange.900">
                          {formatCurrencyWithCode(
                            selectedBatch.payment_summary?.total_settlement_amount || 0,
                            selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency || getDefaultCurrency()
                          )}
                        </Text>
                      </Box>
                    </SimpleGrid>

                    <Divider />

                    {/* Total Cost */}
                    <Box p={4} bg="blue.50" borderRadius="md" border="2px solid" borderColor="blue.300">
                      <HStack justify="space-between">
                        <HStack spacing={2}>
                          <FiDollarSign color="blue.600" size={18} />
                          <Text fontSize="md" fontWeight="700" color="blue.900">
                            Total Company Cost:
                          </Text>
                        </HStack>
                        <Text fontSize="xl" fontWeight="700" color="blue.900">
                          {formatCurrencyWithCode(
                            calculateTotalCompanyCost(selectedBatch),
                            selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency || getDefaultCurrency()
                          )}
                        </Text>
                      </HStack>
                    </Box>

                    {/* Wallet Balance Check */}
                    {walletData && (
                      <Box>
                        <VStack align="stretch" spacing={2}>
                          <HStack justify="space-between" mb={2}>
                            <Text fontSize="sm" fontWeight="600" color="gray.700">
                              Available Balance:
                            </Text>
                            <Text fontSize="sm" fontWeight="700" color={hasSufficientFunds(selectedBatch) ? "green.600" : "red.600"}>
                              {formatCurrencyWithCode(
                                walletData.available_balance ?? walletData.balance ?? walletData.current_balance ?? 0,
                                walletData.currency || selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency || getDefaultCurrency()
                              )}
                            </Text>
                          </HStack>
                          {walletData.pending_settlement !== undefined && walletData.pending_settlement > 0 && (
                            <HStack justify="space-between">
                              <Text fontSize="sm" color="gray.600">
                                Pending Settlement:
                              </Text>
                              <Text fontSize="sm" color="orange.600">
                                {formatCurrencyWithCode(
                                  walletData.pending_settlement,
                                  walletData.currency || selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency || getDefaultCurrency()
                                )}
                                <Text as="span" fontSize="xs" ml={1} color="gray.500">
                                  (available in 1-5 days)
                                </Text>
                              </Text>
                            </HStack>
                          )}
                        </VStack>
                        {hasSufficientFunds(selectedBatch) === false && (
                          <Alert status="error" borderRadius="md" fontSize="sm" mt={2}>
                            <AlertIcon />
                            <AlertDescription>
                              Insufficient available balance. Available: {formatCurrencyWithCode(
                                walletData.available_balance ?? walletData.balance ?? walletData.current_balance ?? 0,
                                walletData.currency || selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency || getDefaultCurrency()
                              )}, Required: {formatCurrencyWithCode(
                                calculateTotalCompanyCost(selectedBatch),
                                walletData.currency || selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency || getDefaultCurrency()
                              )}.
                              {walletData.pending_settlement > 0 && ` Pending settlement: ${formatCurrencyWithCode(
                                walletData.pending_settlement,
                                walletData.currency || selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency || getDefaultCurrency()
                              )} (not yet available).`} Please wait for settlement or fund your wallet.
                            </AlertDescription>
                          </Alert>
                        )}
                        {hasSufficientFunds(selectedBatch) === true && (
                          <Alert status="info" borderRadius="md" fontSize="sm" bg="green.50" borderColor="green.200" mt={2}>
                            <AlertIcon color="green.500" />
                            <AlertDescription color="green.700">
                              Sufficient funds available. Payment processing can proceed.
                            </AlertDescription>
                          </Alert>
                        )}
                      </Box>
                    )}

                    {/* Payment Method Selection */}
                    <Divider />
                    <Box>
                      <FormControl isRequired isInvalid={!selectedPaymentMethod}>
                        <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={3}>
                          Select Payment Method for This Transaction *
                        </FormLabel>
                        {!selectedPaymentMethod && (
                          <Alert status="warning" borderRadius="md" mb={3} fontSize="sm">
                            <AlertIcon />
                            <AlertDescription>
                              Please select a payment method to proceed
                            </AlertDescription>
                          </Alert>
                        )}
                        <RadioGroup
                          value={selectedPaymentMethod || ""}
                          onChange={setSelectedPaymentMethod}
                        >
                          <Stack spacing={3}>
                            {/* Wallet Option */}
                            <Box
                              p={3}
                              border="2px solid"
                              borderColor={selectedPaymentMethod === "wallet" ? "blue.500" : "gray.200"}
                              borderRadius="md"
                              bg={selectedPaymentMethod === "wallet" ? "blue.50" : "white"}
                              cursor="pointer"
                              _hover={{ borderColor: "blue.300" }}
                              onClick={() => setSelectedPaymentMethod("wallet")}
                            >
                              <HStack spacing={3}>
                                <Radio value="wallet" colorScheme="blue" />
                                <VStack align="start" spacing={1} flex={1}>
                                  <HStack>
                                    <FiDollarSign size={18} color={selectedPaymentMethod === "wallet" ? "#3182ce" : "#718096"} />
                                    <Text fontWeight="600" fontSize="sm">
                                      Wallet Funding
                                    </Text>
                                    {paymentMethodData?.payment_method === "wallet" && (
                                      <Badge colorScheme="blue" variant="subtle" size="sm">
                                        Your Default
                                      </Badge>
                                    )}
                                  </HStack>
                                  {walletData && (
                                    <Text fontSize="xs" color="gray.600">
                                      Available: {formatCurrencyWithCode(
                                        walletData.available_balance ?? walletData.balance ?? walletData.current_balance ?? 0,
                                        walletData.currency || selectedBatch?.items?.[0]?.currency || selectedBatch?.batch_items?.[0]?.currency || selectedBatch?.currency || getDefaultCurrency()
                                      )}
                                    </Text>
                                  )}
                                </VStack>
                              </HStack>
                            </Box>

                            {/* Direct Debit Option */}
                            <Box
                              p={3}
                              border="2px solid"
                              borderColor={
                                selectedPaymentMethod === "direct_debit"
                                  ? hasActiveMandate
                                    ? "blue.500"
                                    : "orange.500"
                                  : "gray.200"
                              }
                              borderRadius="md"
                              bg={
                                selectedPaymentMethod === "direct_debit"
                                  ? hasActiveMandate
                                    ? "blue.50"
                                    : "orange.50"
                                  : "white"
                              }
                              cursor={hasActiveMandate ? "pointer" : "not-allowed"}
                              opacity={hasActiveMandate ? 1 : 0.7}
                              _hover={hasActiveMandate ? { borderColor: "blue.300" } : {}}
                              onClick={() => {
                                if (hasActiveMandate) {
                                  setSelectedPaymentMethod("direct_debit");
                                }
                              }}
                            >
                              <HStack spacing={3}>
                                <Radio
                                  value="direct_debit"
                                  colorScheme="blue"
                                  isDisabled={!hasActiveMandate}
                                />
                                <VStack align="start" spacing={1} flex={1}>
                                  <HStack>
                                    <FiCreditCard
                                      size={18}
                                      color={
                                        selectedPaymentMethod === "direct_debit"
                                          ? hasActiveMandate
                                            ? "#3182ce"
                                            : "#dd6b20"
                                          : "#718096"
                                      }
                                    />
                                    <Text fontWeight="600" fontSize="sm">
                                      Direct Debit
                                    </Text>
                                    {paymentMethodData?.payment_method === "direct_debit" && hasActiveMandate && (
                                      <Badge colorScheme="blue" variant="subtle" size="sm">
                                        Your Default
                                      </Badge>
                                    )}
                                    {hasActiveMandate && mandate?.status === "active" && (
                                      <Badge colorScheme="green" variant="subtle" size="sm">
                                        Active
                                      </Badge>
                                    )}
                                    {!hasActiveMandate && (
                                      <Badge colorScheme="orange" variant="subtle" size="sm">
                                        Setup Required
                                      </Badge>
                                    )}
                                  </HStack>
                                  {hasActiveMandate && mandate && (
                                    <Text fontSize="xs" color="gray.600">
                                      Account: {mandate.account_number} ({mandate.bank_name})
                                    </Text>
                                  )}
                                  {!hasActiveMandate && (
                                    <Text fontSize="xs" color="orange.600">
                                      Please set up Direct Debit in Settings first
                                    </Text>
                                  )}
                                </VStack>
                              </HStack>
                            </Box>
                          </Stack>
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  </>
                )}
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onApproveClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="green" 
                onClick={handleApproveBatch} 
                ml={3} 
                isLoading={approveBatchMutation.isPending || updatePaymentMethodIsLoading}
                leftIcon={<FiCheckCircle />}
                isDisabled={
                  !selectedPaymentMethod ||
                  (selectedPaymentMethod === "wallet" && hasSufficientFunds(selectedBatch) === false) ||
                  (selectedPaymentMethod === "direct_debit" && !hasActiveMandate)
                }
              >
                Approve & Process
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Reject Confirmation Dialog */}
      <AlertDialog
        isOpen={isRejectOpen}
        leastDestructiveRef={cancelRef}
        onClose={onRejectClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Reject Payroll Batch
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to reject this payroll batch? The batch will be sent back to HR for re-approval.
              <Box mt={3} p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" fontWeight="600">Batch: {selectedBatch?.batch_name}</Text>
                <Text fontSize="sm">Total Amount: {formatCurrencyWithCode(selectedBatch?.total_amount || 0, selectedBatch?.items?.[0]?.currency || selectedBatch?.batch_items?.[0]?.currency || selectedBatch?.currency || getDefaultCurrency())}</Text>
                <Text fontSize="sm">Employees: {selectedBatch?.employee_count || 0}</Text>
              </Box>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onRejectClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleRejectBatch} ml={3} isLoading={rejectBatchMutation.isPending}>
                Reject
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Success Modal */}
      <AlertDialog
        isOpen={isSuccessOpen}
        leastDestructiveRef={cancelRef}
        onClose={onSuccessClose}
        size="xl"
      >
        <AlertDialogOverlay>
          <AlertDialogContent maxW="600px">
            <AlertDialogHeader fontSize="xl" fontWeight="bold" pb={2}>
              <HStack spacing={2}>
                <FiCheckCircle color="green" size={24} />
                <Text>Payment Processed Successfully!</Text>
              </HStack>
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack spacing={4} align="stretch">
                {/* Success Message */}
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    Batch approved and payment initiated successfully. The batch is now being processed.
                  </AlertDescription>
                </Alert>

                {/* Payment Details */}
                {approvalSuccessData && (
                  <Box p={4} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                    <VStack align="start" spacing={3}>
                      <HStack spacing={2} w="100%">
                        <Text fontSize="sm" fontWeight="600" color="gray.700" minW="140px">
                          Batch ID:
                        </Text>
                        <Text fontSize="sm" color="gray.800" fontWeight="500">
                          {approvalSuccessData.batchName || approvalSuccessData.batchId}
                        </Text>
                      </HStack>
                      
                      <HStack spacing={2} w="100%">
                        <Text fontSize="sm" fontWeight="600" color="gray.700" minW="140px">
                          Payment Method:
                        </Text>
                        <Badge colorScheme={approvalSuccessData.paymentMethod === 'direct_debit' ? 'blue' : 'green'}>
                          {approvalSuccessData.paymentMethod === 'direct_debit' ? 'Direct Debit' : 'Wallet'}
                        </Badge>
                      </HStack>
                      
                      <HStack spacing={2} w="100%">
                        <Text fontSize="sm" fontWeight="600" color="gray.700" minW="140px">
                          Total Amount:
                        </Text>
                        <Text fontSize="sm" color="gray.800" fontWeight="600">
                          {formatCurrencyWithCode(approvalSuccessData.totalAmount || 0, approvalSuccessData.currency || 'NGN')}
                        </Text>
                      </HStack>
                      
                      <HStack spacing={2} w="100%">
                        <Text fontSize="sm" fontWeight="600" color="gray.700" minW="140px">
                          Employees:
                        </Text>
                        <Text fontSize="sm" color="gray.800">
                          {approvalSuccessData.employeeCount || 0}
                        </Text>
                      </HStack>
                      
                      {approvalSuccessData.transactionRef && (
                        <HStack spacing={2} w="100%">
                          <Text fontSize="sm" fontWeight="600" color="gray.700" minW="140px">
                            Transaction Ref:
                          </Text>
                          <Text fontSize="sm" color="gray.800" fontFamily="mono">
                            {approvalSuccessData.transactionRef}
                          </Text>
                        </HStack>
                      )}
                    </VStack>
                  </Box>
                )}

                {/* Next Steps */}
                <Box p={3} bg="gray.50" borderRadius="md">
                  <Text fontSize="sm" color="gray.700" fontWeight="600" mb={2}>
                    Next Steps:
                  </Text>
                  <VStack align="start" spacing={1}>
                    <Text fontSize="xs" color="gray.600">
                      • Batch is being processed in the background
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      • Check status in the "Batch Management" tab
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      • You'll receive a notification when processing completes
                    </Text>
                  </VStack>
                </Box>
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button 
                colorScheme="blue" 
                onClick={() => {
                  onSuccessClose();
                  setApprovalSuccessData(null);
                }}
              >
                Close
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
};

export default AccountantApprovals;